import React from "react";
import {
  Box,
  Typography,
  LinearProgress,
  useTheme,
  Tooltip,
} from "@mui/material";

interface ProgressBarStats {
  totalMembers: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  bounced: number;
  unsubscribed: number;
  prospectValue?: number;
  totalReplies?: number;
  totalBookings?: number;
}

interface ProgressBarProps {
  value: number; // Progress value (0-100)
  total: number; // Total count
  completed: number; // Completed count
  showLabel?: boolean; // Whether to show the percentage label
  height?: number; // Height of the progress bar
  stats?: ProgressBarStats; // Optional detailed stats for tooltip
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  total,
  completed,
  showLabel = true,
  height = 8,
  stats,
}) => {
  const theme = useTheme();

  // Calculate percentage
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Calculate bounce percentage
  const bouncePercentage =
    stats && stats.totalMembers > 0
      ? Math.round((stats.bounced / stats.totalMembers) * 100)
      : 0;

  const tooltipContent = stats ? (
    <Box sx={{ p: 2, minWidth: 280 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, mb: 2, fontSize: "0.9rem" }}
      >
        Sequence Progress
      </Typography>

      {/* First 2x2 Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Total Members
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.totalMembers}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Completed
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.completed}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            In Progress
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.inProgress}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Not Started
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.notStarted}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.2)", my: 1.5 }} />

      {/* Second 2x2 Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Bounced
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.bounced}{" "}
            <Typography
              component="span"
              sx={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)" }}
            >
              ({bouncePercentage}%)
            </Typography>
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Unsubscribed
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {stats.unsubscribed}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.2)", my: 1.5 }} />

      {/* Progress percentage - same format as others */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
          >
            Progress
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {percentage}%
          </Typography>
        </Box>

        {stats.prospectValue && stats.prospectValue > 0 && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255, 255, 255, 0.7)", display: "block" }}
            >
              Pipeline Value
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              ${(((stats.totalReplies || 0) + (stats.totalBookings || 0)) * stats.prospectValue).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    ""
  );

  const progressBar = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: "0.75rem",
            mb: 0.5,
          }}
        >
          {percentage}%
        </Typography>
      )}
      <Box sx={{ width: "100%", position: "relative" }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: height,
            borderRadius: 1,
            backgroundColor: theme.palette.action.hover,
            "& .MuiLinearProgress-bar": {
              backgroundColor: theme.palette.primary.main,
              borderRadius: 1,
            },
          }}
        />
      </Box>
    </Box>
  );

  return stats ? (
    <Tooltip title={tooltipContent} arrow placement="top">
      {progressBar}
    </Tooltip>
  ) : (
    progressBar
  );
};

export default ProgressBar;
